import dayjs from 'dayjs'
import _ from 'lodash'
import { useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { useTranslation } from 'react-i18next'

import { Button, IconButton, Portal, Stack, Tooltip, Typography } from '@mui/material'
import HorizontalRuleRoundedIcon from '@mui/icons-material/HorizontalRuleRounded'
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt'
import RefreshIcon from '@mui/icons-material/Refresh'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'

import DoctorIcon from '@/components/ui/icons/Doctor'
import NurseIcon from '@/components/ui/icons/Nurse'
import { Consultation } from '@/core/models/consultation.model'
import { useGetConsultationQuery } from '@/core/services/consultation'
import { useCurrentTime } from '@/hooks/useCurrentTime'
import { useIsDoctor, useIsPds } from '@/hooks/useStaffRole'
import { useDiagnosticLayout } from '@/layouts/diagnostic/DiagnosticLayout'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { Room } from '@/core/models/room.model'
import EmergencyDoctorSelectionDialog from '@/pages/diagnostic/containers/EmergencyDoctorSelectionDialog'
import NetworkSpeed from '@/pages/diagnostic/containers/NetworkSpeed'
import InvitationDialog from '@/pages/diagnostic/containers/InvitationDialog'

import { LeaveEmergencyButton } from './LeaveEmergencyButton'
import { useRefreshEmergencyRoom } from '../model/useRefreshEmergencyRoom'

type TopbarProps = {
  emergencyRoom: Room
}

export function Topbar({ emergencyRoom }: TopbarProps) {
  const { t } = useTranslation()

  const { topbarContentContainer } = useDiagnosticLayout()

  const authUser = useAppSelector(selectAuthUser)

  const { data: emergencyConsultation } = useGetConsultationQuery(emergencyRoom.currentConsultation ?? skipToken)

  const isDoctor = useIsDoctor()
  const isPds = useIsPds()
  const isRoomCreator = authUser?._id === (typeof emergencyRoom.creator === 'string' ? emergencyRoom.creator : emergencyRoom.creator._id)

  if (!isPds && emergencyRoom.status === 'inactive') {
    return (
      <Portal container={topbarContentContainer}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}></Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <RefreshButton emergencyRoom={emergencyRoom} />
            <LeaveEmergencyButton emergencyRoom={emergencyRoom} />
          </Stack>
        </Stack>
      </Portal>
    )
  }

  return (
    <Portal container={topbarContentContainer}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flex: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
          <Typography variant="h1" sx={{ color: (theme) => theme.palette.common.white, fontSize: '16px', fontWeight: 500 }}>
            {t('page.emergency.title')}
          </Typography>
          {isPds && !!emergencyConsultation && <DoctorInfo emergencyConsultation={emergencyConsultation} />}
          {isDoctor && !!emergencyConsultation && <PdsInfo emergencyConsultation={emergencyConsultation} />}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={2}>
          {isPds && isRoomCreator && !!emergencyConsultation && (
            <>
              <InviteButton emergencyRoomId={emergencyRoom._id} />
              <EmergencyDoctorSelectionDialog consultationId={emergencyConsultation._id} title={t('page.emergency.doctorSelectionDialog.title')} />
            </>
          )}
          <NetworkSpeed />
          <LeaveEmergencyButton emergencyRoom={emergencyRoom} />
        </Stack>
      </Stack>
    </Portal>
  )
}

function PdsInfo({ emergencyConsultation }: { emergencyConsultation: Consultation }) {
  return (
    <>
      <HorizontalRuleRoundedIcon />
      <Stack alignItems="center" justifyContent="center" direction="row" spacing={1}>
        <NurseIcon htmlColor="#FFF" />
        <Typography variant="h2" sx={{ color: (theme) => theme.palette.common.white, fontSize: '14px', fontWeight: 500 }}>
          {_.trim(`${emergencyConsultation.pds?.userInfo.name || ''} ${emergencyConsultation.pds?.userInfo.surname || ''}`)}
        </Typography>
        {!!emergencyConsultation.medecin && (
          <>
            <DoctorIcon htmlColor="#FFF" />
            <Typography variant="h2" sx={{ color: (theme) => theme.palette.common.white, fontSize: '14px', fontWeight: 500 }}>
              {`${emergencyConsultation.medecin.userInfo.name} ${emergencyConsultation.medecin.userInfo.surname}`}
            </Typography>
          </>
        )}
        <TimerOutlinedIcon />
        <Timestamp startDate={emergencyConsultation.startDate} />
      </Stack>
    </>
  )
}

function DoctorInfo({ emergencyConsultation }: { emergencyConsultation: Consultation }) {
  if (!emergencyConsultation.medecin) return null

  return (
    <>
      <HorizontalRuleRoundedIcon />
      <Stack alignItems="center" justifyContent="center" direction="row" spacing={1}>
        <DoctorIcon htmlColor="#FFF" />
        <Typography variant="h2" sx={{ color: (theme) => theme.palette.common.white, fontSize: '14px', fontWeight: 500 }}>
          {`${emergencyConsultation.medecin.userInfo?.name} ${emergencyConsultation.medecin.userInfo?.surname}`}
        </Typography>
        <TimerOutlinedIcon />
        <Timestamp startDate={emergencyConsultation.startDate} />
      </Stack>
    </>
  )
}

function InviteButton({ emergencyRoomId }: { emergencyRoomId: string }) {
  const { t } = useTranslation()

  const [openInvitationDialog, setOpenInvitationDialog] = useState(false)

  return (
    <>
      <Button
        variant="contained"
        sx={{ padding: '7px', borderColor: 'white', borderRadius: '15px', fontSize: '12px' }}
        onClick={() => setOpenInvitationDialog(true)}
      >
        {t('page.emergency.openParticipantsButton.label')}
        <PersonAddAltIcon htmlColor="#FFF" sx={{ ml: '10px', fontSize: '20px' }} />
      </Button>
      <InvitationDialog open={openInvitationDialog} onClose={() => setOpenInvitationDialog(false)} roomId={emergencyRoomId} />
    </>
  )
}

function RefreshButton({ emergencyRoom }: { emergencyRoom: Room }) {
  const { t } = useTranslation()
  const refreshRoom = useRefreshEmergencyRoom(emergencyRoom)
  return (
    <Tooltip title={t('page.emergency.refreshRoomButton.tooltip')}>
      <IconButton onClick={refreshRoom}>
        <RefreshIcon htmlColor="#FFF" />
      </IconButton>
    </Tooltip>
  )
}

function Timestamp({ startDate }: { startDate: string }) {
  const currentTime = useCurrentTime()
  return (
    <Typography component="p" sx={{ color: (theme) => theme.palette.common.white, fontSize: '14px', fontWeight: 500 }}>
      {dayjs.duration(Math.abs(currentTime.diff(dayjs(startDate)))).format('HH[h]mm')}
    </Typography>
  )
}
